/* ============================================================
   Freebird Stores — change a cart line's size in place
   ============================================================
   Figma 4a (node 459:32199) puts a size dropdown on every cart line.
   Shopify has no operation for that: the Cart API can change a line's
   quantity and properties but not its `variant_id`. So a size change
   is an ADD of the new variant plus a REMOVE of the old one, and the
   interesting part of this file is the order those happen in and what
   happens when the cart already holds the size being asked for.

   Plan: docs/plans/2026-09-08-001-feat-cart-page-figma-build-plan.md
   Ticket: DEV-96

   ── LINES ARE ADDRESSED BY KEY, NEVER BY INDEX ────────────────
   `/cart/change.js` accepts a 1-based `line`, and every write here
   would be a bug if it used one: the first write shifts every later
   line's index, so the second write lands on the wrong row. Line item
   KEYS are stable across the whole sequence. Liquid writes each line's
   key onto its own select.

   ── ADD BEFORE REMOVE, AND THAT ORDER IS THE SAFETY ───────────
   The two writes are separate requests and nothing makes them atomic,
   so one of them can be the last thing that succeeds. Remove-then-add
   fails by LOSING the shopper's item; add-then-remove fails by leaving
   them with two lines, which they can see and fix. Same reasoning
   picks the order inside the merge case below.

   ── THE CHANGED LINE MOVES TO THE TOP OF THE CART ─────────────
   MEASURED, NOT ASSUMED, AND THE ASSUMPTION WAS WRONG. This repo said
   in four places that `/cart/add.js` APPENDS and the moved line lands
   at the END. It does not. Cleared cart, added size 6, added size 9,
   read the rendered page: size 9 renders FIRST. Shopify puts a newly
   added line at the head of `cart.items`, and the theme applies no
   ordering of its own — `snippets/cart-products.liquid` iterates
   `cart.items` with no filter.

   So a size change makes the line the newest and it jumps to the top,
   which reads to a shopper as the item being re-added rather than
   edited. A consequence of add-then-remove; the Cart AJAX API offers
   no positional add and no reorder. Accepted for now — KTD2 in the
   plan reached the same conclusion from the wrong premise.

   ── WHY THE WHOLE SURFACE RE-RENDERS ──────────────────────────
   `assets/component-cart-items.js` resolves rows POSITIONALLY —
   `this.refs.cartItemRows[line - 1]`, and `#handleCartError` the same
   way. After a reorder those indices point at the wrong rows, and the
   symptom is a later action animating a row that did not change while
   the cart mutation itself stays correct. A targeted row update cannot
   fix that; a full re-render can, and `assets/freebird-cart-sync.js`
   already owns one.
   ============================================================ */

/* THE RE-RENDER IS BORROWED, NOT REBUILT.
   `assets/freebird-cart-sync.js` publishes it on `window.Freebird`
   rather than as an ES export because base's import map
   (`snippets/scripts.liquid`) has no entry for it and claiming that
   file for one alias is a permanent ledger cost for no behaviour; that
   file's own comment carries the full reasoning. Read at call time, not
   at module load, so module execution order cannot matter.

   If it is ever missing, the cart mutation has already succeeded — so
   fall back to a full reload rather than leaving the shopper looking at
   a stale cart. */
/* HAND-KEPT TWIN of the identical guard in assets/freebird-cart-undo.js. Both were left
   duplicated rather than consolidated, and the reason is structural
   rather than a judgement call: this guard's whole job is to cope with
   `assets/freebird-cart-sync.js` being absent, so it cannot live there.
   Extracting it to a fourth asset would add a file and a script tag to
   share fourteen lines, and would give that file the same
   is-it-loaded problem one level down.

   The repo already names this shape — see the note on
   `snippets/freebird-cart-line-is-coverage.liquid`, whose JS twin in
   cart-sync carries the same warning. CHANGE ONE AND CHANGE THE OTHER;
   nothing enforces it. The only intended difference is the warning
   text, which names what the shopper would otherwise not see. */
const rerender = (cart) => {
  const shared = window.Freebird?.requestCartRerender;
  if (typeof shared === 'function') {
    shared(cart);
    return;
  }
  console.warn('[Freebird] cart sync unavailable; reloading to show the change');
  window.location.reload();
};

const SELECT_SELECTOR = '[data-fb-cart-size]';

/* Both cart surfaces render the same rows, so on /cart every row-level
   selector matches TWICE — the drawer's copy comes first in document
   order because `#header-group` precedes `<main>`. Every query in this
   file that reaches for a row must say which surface it means.
   `assets/freebird-cart-undo.js` has always scoped this way; this file
   is the one that drifted. */
const CART_PAGE_SELECTOR = '.cart-page';

/* Concurrency guard. One in-flight swap at a time across the whole
   page: two overlapping swaps would each have read the cart before the
   other's write landed, and the second's merge decision would be based
   on a cart that no longer exists. */
let inFlight = false;

const cartRoot = () => window.Shopify?.routes?.root ?? '/';

/* A HUNG REQUEST IS WORSE HERE THAN A FAILED ONE. Without a timeout,
   `inFlight` stays true and the select stays `disabled` for as long as
   the socket hangs — the shopper cannot change any size on the page
   again until they reload, with no error to explain why. 15s is longer
   than any cart write should need and shorter than a shopper's patience
   for a dead control. `AbortSignal.timeout` rejects the fetch, so the
   existing catch reverts the select and shows the message. */
const REQUEST_TIMEOUT_MS = 15000;

/* How long the focus hand-off waits for the swapped line to appear
   before focusing anything. Matches `LATE_RENDER_TIMEOUT_MS` in
   assets/freebird-cart-sync.js — the window that file's observed wait
   allows a re-render — and the twin constant in
   assets/freebird-cart-undo.js. Waiting less would give up while the
   observer was still watching. */
const FOCUS_SETTLE_MS = 3000;

const postJson = async (path, body) => {
  const response = await fetch(`${cartRoot()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  /* GATE ON `response.ok`, NOT ON PARSING THE BODY. This store has
     served Cloudflare challenge pages under load, and those come back
     with an HTML body and a non-200 status; a check that only tried to
     parse JSON would read the challenge as a malformed cart. */
  if (!response.ok) {
    let message = `${path} ${response.status}`;
    try {
      const payload = await response.json();
      if (payload?.description) message = payload.description;
    } catch {
      /* Non-JSON body — keep the status-code message. */
    }
    throw new Error(message);
  }
  return response.json();
};

const fetchCart = async () => {
  const response = await fetch(`${cartRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
};

/* Two lines can only merge if they agree on EVERYTHING a shopper chose,
   not just the variant. A monogrammed pair and a plain pair of the same
   boot are different things to buy, and Shopify keeps them as separate
   lines for exactly that reason — merging them would silently discard
   one line's monogram. */
/* PROPERTIES ARE NORMALISED TO AN OBJECT BEFORE THEY GO ANYWHERE.
   An empty line-item property set does not serialise as `{}` — the
   cartpage walkthrough run captured Liquid emitting
   `data-fb-undo-properties="[]"`, an empty ARRAY, and `/cart.js` is
   equally free to return `null`. `/cart/add.js` documents `properties`
   as a hash, so posting `[]` is sending the wrong JSON type for a value
   that merely looks empty either way.

   `Object.keys([])` is also `[]`, so the equality test below happens to
   survive an array — which is exactly why this was invisible until the
   value reached the API. Normalise once, at the boundary. */
const asProperties = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const samePropertiesAs = (properties) => (item) => {
  const a = asProperties(properties);
  const b = asProperties(item.properties);
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => String(a[key]) === String(b[key]));
};

/* The row's own error cell, which base already renders hidden and
   `assets/component-cart-items.js` already writes into for quantity
   failures. Reused rather than adding a second error surface, so a
   failed size change reports where a shopper has already been taught to
   look. */
/* SCOPED TO THE SURFACE THE SELECT IS IN, and a document-wide lookup
   here was a shopper-facing money bug. On /cart these refs exist TWICE:
   the drawer renders its own copy of every row from
   snippets/header-actions.liquid, inside `#header-group`, which comes
   FIRST in document order. So `document.querySelector` wrote every
   failure into a `<td>` inside a closed `<dialog>` and the page's own
   error cell stayed hidden.

   What that cost: the add-then-remove swap is ordered so that a failed
   remove leaves the shopper holding BOTH sizes — visible, and fixable
   with the Remove control two rows down — but only if they are told.
   They were not. The cart re-rendered showing two sizes of the same
   boot and a subtotal up by a full pair, with no message anywhere. */
const surfaceOf = (element) => element?.closest('cart-items-component') ?? document;

/* ── EVERY ROW-LEVEL LOOKUP GOES THROUGH ONE OF THESE TWO ─────────
   On /cart the drawer renders a full copy of every row and comes FIRST
   in document order, because `#header-group` precedes `<main>`. So a
   bare `document.querySelector` for anything row-shaped resolves to the
   drawer's hidden copy, and the symptom is always the same: an action
   that silently does nothing, or a message written where nobody can see
   it. That defect has now been introduced three separate times in this
   file — the error cell, the focus target, and the clamp notice — twice
   by the commit that fixed the previous one.

   The rule that replaces vigilance: NO RAW `document.querySelector` FOR
   A ROW ELEMENT ANYWHERE IN THIS FILE. Ask for a surface, or ask for
   the visible ones. */
const inSurface = (surface, selector) => [...(surface ?? document).querySelectorAll(selector)];

const visibleInSurface = (surface, selector) =>
  inSurface(surface, selector).filter(
    (el) => el instanceof HTMLElement && el.offsetParent !== null
  );

/* ── AND EVERY MESSAGE GOES THROUGH THIS ──────────────────────────
   The row's error cell lives INSIDE the section that `rerender`
   replaces, so anything written before a re-render survives about
   300ms. That has also been introduced twice: the clamp notice was
   built to avoid it, and the failure path — the one where the shopper
   is holding two sizes and most needs telling — kept writing inline.

   Deferring is therefore not the caller's job. This waits for the
   re-render whether or not one is coming (`afterCartRerender` runs the
   callback immediately when it finds no root), re-resolves the row in
   the surface that exists afterwards, and only then writes. A caller
   cannot get the order wrong because there is no order to get. */
const showLineMessageAfterRender = (surface, line, message) => {
  const write = () => {
    /* The captured surface is detached after a morph. Re-resolve from a
       live row carrying the same cart line number, and fall back to the
       captured one when the row has genuinely gone. */
    const liveRow = visibleInSurface(document, `[data-cart-line="${line}"]`)[0];
    const liveSurface = liveRow ? surfaceOf(liveRow) : surface;
    showLineError(liveSurface, line, message);
  };
  const observed = window.Freebird?.afterCartRerender;
  if (typeof observed === 'function') observed(write);
  else requestAnimationFrame(write);
};

const showLineError = (surface, line, message) => {
  const container = surface.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const target = surface.querySelector(`[ref="cartItemError-${line}"]`);
  if (!container || !target) {
    console.warn('[Freebird] cart size change failed', message);
    return;
  }
  target.textContent = message;
  container.classList.remove('hidden');
};

const clearLineError = (surface, line) => {
  const container = surface.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  container?.classList.add('hidden');
};

/* Focus has to be put back deliberately. The re-render replaces the
   section's whole subtree, so the <select> the shopper was using is a
   different element afterwards and the browser has already dropped
   focus to <body> — the defect this theme has shipped twice. The
   changed line has MOVED — to the TOP, see the header — so it is found
   by variant id rather than by the index it used to have. Finding it by
   id is what made this survive the wrong assumption about which end. */
const restoreFocus = (surface, variantId) => {
  /* WAIT FOR THE SWAP, NOT FOR A FRAME. This used a single
     `requestAnimationFrame`, which does not work and would have been
     the third outing of this theme's focus-drops-to-<body> defect.
     `assets/freebird-cart-focus.js` already documents why: base's
     `#handleCartUpdate` re-renders through
     `sectionRenderer.renderSection(id, { cache: false })`, awaiting a
     NETWORK FETCH, so any frame callback runs while the old DOM is
     still perfectly intact — it focuses an element that is about to be
     destroyed, and focus lands on <body> when it is.

     `afterCartRerender` is the observed wait, shared from cart-sync so
     this and the undo controller cannot drift apart on it.

     Falls back to focusing anything if the helper is missing, since at
     that point `rerender` has already reloaded the page and this is
     moot. */
  /* WAIT FOR THE SWAPPED LINE, DO NOT FOCUS WHATEVER IS THERE. Same
     rule and same reasoning as `focusRestoredLine` in
     assets/freebird-cart-undo.js, which this deliberately mirrors — the
     two files share the observed wait so they must share what they do
     when it wakes early. Any early wake (a late server re-render, a
     Redo write, the helper's own 3s fallback) leaves this running
     against the pre-swap DOM, where `selects[selects.length - 1]` is a
     real element that the morph is about to destroy. Focusing it puts
     focus on <body> a frame later, by way of the fallback meant to
     prevent that.

     A fallback chain cannot answer a TIMING question. So the wait ends
     when the swapped line EXISTS: its select reports the new variant as
     its value, which no pre-swap element does. The chain stays for past
     the deadline, where the re-render genuinely never came and anything
     focusable beats <body>. */
  const settle = (deadline) => {
    /* SCOPED AND VISIBILITY-FILTERED UP FRONT, and neither was true.

       The unscoped query reached the drawer's copy of every row. Those
       selects sit in a closed <dialog>, so they matched while being
       unfocusable: `find` returned one, `.focus()` did nothing, and the
       poll span its full deadline because the same hidden element kept
       matching first on every frame.

       AND THE MATCH ITSELF WAS WRONG. This looked for a select whose
       `value` is the target, on the stated premise that no pre-swap
       element reports it. The select the shopper just changed reports
       exactly that — it is why the change event fired. Only
       `data-current-variant`, written by Liquid and still holding the
       PREVIOUS variant until the row re-renders, separates the two DOM
       states. On /cart the observed wait hid this; in the drawer, where
       `afterCartRerender` finds no root and fires synchronously, it did
       not. */
    /* THE SURFACE THE SHOPPER ACTED IN, not a hard-coded `.cart-page`.
       Scoping to the page fixed /cart and broke the drawer, which
       renders the same selects: `settle` found nothing there, span its
       full deadline, and left focus on <body> — the defect it exists to
       prevent, moved to the surface neither the walkthrough nor the
       review covered. */
    const selects = visibleInSurface(surface, SELECT_SELECTOR);
    const moved = selects.find((select) => select.dataset.currentVariant === String(variantId));
    if (moved) {
      moved.focus();
      return;
    }
    if (Date.now() < deadline) {
      requestAnimationFrame(() => settle(deadline));
      return;
    }
    /* Every candidate is already visible and already on this surface —
       the filter above did that. `selects[0]` off an UNFILTERED list was
       the drawer's first select: non-null, hidden, and so a silent
       no-op that left focus on <body> while reading like a fallback
       that had fired. */
    const target = selects[selects.length - 1] ?? visibleInSurface(surface, '.cart-items__remove')[0];
    target?.focus();
  };

  const restore = () => settle(Date.now() + FOCUS_SETTLE_MS);
  const observed = window.Freebird?.afterCartRerender;
  /* WATCH THE SURFACE THAT WILL RE-RENDER. `afterCartRerender` defaults
     to a `.cart-page` root and runs its callback IMMEDIATELY when it
     finds none — so in the drawer the wait did not wait, and `settle`
     ran against the pre-swap DOM. */
  if (typeof observed === 'function') observed(restore, 'cart-items-component');
  else requestAnimationFrame(restore);
};

/* ── THE RENDERED ORDER, WRITTEN AS A CART ATTRIBUTE ──────────────
   Shopify puts a newly added line at the head of `cart.items`, so a
   swap — which is an add plus a remove — moves the edited line to the
   top. Shoppers read that as the item being re-added rather than
   edited. Measured, not assumed:
   docs/solutions/theme-architecture/a-cart-add-lands-at-the-top-not-the-end.md

   IT RIDES ALONG IN THE WRITE THIS FILE ALREADY MAKES. `/cart/update.js`
   takes `attributes` in the same call that removes the old line, so the
   order costs no extra request. That matters more than it sounds: this
   store rate-limits `/cart.js` and 429s `/cart/add.js` under
   contention, and a design that spends another request to fix a
   cosmetic problem is the wrong trade.

   ── BY VARIANT ID, NOT BY LINE KEY, AND THAT IS FORCED ────────────
   A line key is the better identifier and it cannot be used here. The
   add creates a NEW line with a NEW key, and that key is only knowable
   from the response — after the write the attribute would have to be
   part of. Writing it would take a second request. Variant ids are
   known before the write, which is the whole reason this costs nothing.

   The cost of that choice: two lines holding the same variant with
   different line-item properties — a monogrammed pair and a plain pair
   — are indistinguishable here. The second one falls into the unknown
   bucket and renders at the top. Rare, harmless, and preferable to a
   second write on this store.

   ── A CART ATTRIBUTE, NOT A LINE-ITEM PROPERTY ────────────────────
   Shopify merges lines only when their properties match EXACTLY, so
   stamping a sequence onto lines would silently break the merge case
   this file goes out of its way to handle. A cart attribute is
   cart-level and cannot affect merging. */
const LINE_ORDER_ATTRIBUTE = '_fb_line_order';

/**
 * The variant ids of `cart.items` in their current order, with one
 * substitution applied.
 *
 * @param {object} cart - The cart as read before the write.
 * @param {string} sourceKey - The line being changed.
 * @param {string | null} targetVariantId - The variant it becomes, or
 *   `null` when the line is folding into an existing twin and should
 *   simply drop out.
 * @returns {string} Comma-joined variant ids, ready for the attribute.
 */
/**
 * The lines in the order the shopper is CURRENTLY SEEING them.
 *
 * Replays what `snippets/cart-products.liquid` does at render: the lines
 * the attribute does not name, in cart order, then the named lines in
 * the order named, each line once.
 *
 * @param {object} cart - A cart object, with `attributes`.
 * @returns {object[]} `cart.items`, reordered as rendered.
 */
const renderedOrder = (cart) => {
  const named = String(cart.attributes?.[LINE_ORDER_ATTRIBUTE] ?? '')
    .split(',')
    .filter(Boolean);
  const seen = new Set();
  const ordered = [];

  cart.items.forEach((item) => {
    if (named.includes(String(item.variant_id))) return;
    seen.add(item.key);
    ordered.push(item);
  });

  named.forEach((id) => {
    cart.items.forEach((item) => {
      if (String(item.variant_id) !== id || seen.has(item.key)) return;
      seen.add(item.key);
      ordered.push(item);
    });
  });

  return ordered;
};

/**
 * The variant ids in the order they should render AFTER this swap.
 *
 * ── BUILT FROM THE RENDERED ORDER, NOT FROM `cart.items` ──────────
 * This read `cart.items` directly, which is Shopify's raw order and
 * NOT what the shopper is looking at once an attribute exists. So every
 * swap after the first re-derived the order from a baseline the page
 * had already stopped using, and wrote an attribute describing a cart
 * nobody had seen.
 *
 * The result was worse than having no ordering at all: on a three-line
 * cart, two ordinary size changes moved every row, including the line
 * the shopper had just edited. The mechanism's own symptom, produced by
 * the mechanism.
 *
 * The walkthrough could not catch it — it seeds a fresh cart and
 * performs exactly ONE swap, which is the case that works.
 *
 * ── UNKNOWN IDS ARE DROPPED ON EACH REBUILD ───────────────────────
 * `renderedOrder` only ever returns lines that exist, so a variant that
 * has left the cart falls out here rather than persisting. That also
 * closes the stale-pin case: a line removed and later re-added from a
 * product page goes back to the top with every other new line, instead
 * of being held in a position the attribute still remembered.
 *
 * @param {object} cart - The cart as read before the write.
 * @param {string} sourceKey - The line being changed.
 * @param {string | null} targetVariantId - The variant it becomes, or
 *   `null` when the line is folding into an existing twin and should
 *   simply drop out.
 * @returns {string} Comma-joined variant ids, ready for the attribute.
 */
const lineOrderAfterSwap = (cart, sourceKey, targetVariantId) => {
  const ids = [];
  renderedOrder(cart).forEach((item) => {
    if (item.key === sourceKey) {
      /* The merge case passes null: the source line disappears into the
         twin, which keeps its own position further down the list. */
      if (targetVariantId) ids.push(String(targetVariantId));
      return;
    }
    ids.push(String(item.variant_id));
  });
  return ids.join(',');
};

const swap = async (select) => {
  const lineKey = select.dataset.lineKey;
  const line = Number(select.dataset.line);
  const previousVariantId = select.dataset.currentVariant;
  const targetVariantId = select.value;

  if (!lineKey || targetVariantId === previousVariantId) return;

  inFlight = true;
  select.disabled = true;

  /* Gates the focus hand-off below on the write having succeeded. It
     cannot be inferred from the absence of a throw once that call moved
     outside the `try`. */
  let swapped = false;

  /* Tracks whether the cart was mutated before a failure. This file's
     header claims a half-done swap "is visible and fixable with the
     Remove control two lines below" — which was only true AFTER a
     re-render, and nothing re-rendered on the failure path. The shopper
     got an error above an unchanged cart, and the extra line turned up
     on their next page load with no explanation. */
  let addLanded = false;

  /* A message to show AFTER the re-render, when the row that carries it
     exists again. Set on the paths where the swap succeeded but not
     exactly as asked. */
  let notice = null;

  /* Captured BEFORE the first await, and before the first use. After a
     re-render this select is detached, so `closest` would return null
     and the error would fall back to a document-wide search — the bug
     this exists to close. */
  const surface = surfaceOf(select);
  clearLineError(surface, line);

  try {
    const cart = await fetchCart();
    const source = cart.items.find((item) => item.key === lineKey);
    /* The line is gone — a concurrent removal, or Redo rewriting the
       cart between render and change. Re-render rather than write
       against a cart we have already misread. */
    if (!source) {
      rerender(cart);
      return;
    }

    const quantity = source.quantity;
    const properties = asProperties(source.properties);

    /* MERGE CASE FIRST, because it is the one that corrupts quantities
       if it is bolted on later as a special case: a swap that ignores
       it produces two lines holding the same variant, which Shopify
       permits and a shopper reads as a bug. */
    const twin = cart.items.find(
      (item) =>
        item.key !== lineKey &&
        String(item.variant_id) === targetVariantId &&
        samePropertiesAs(properties)(item)
    );

    if (twin) {
      /* RE-READ IMMEDIATELY BEFORE THE WRITE, because the write is
         ABSOLUTE and `/cart/update.js` offers no conditional-write
         primitive to build a real compare-and-set on: `updates` sets a
         line to a number, and cannot say "set it to 3 only if it is
         still 2". The nearest thing available is to read again as late
         as possible and refuse to write when the answer moved.

         The window this narrows is not theoretical. `fetchCart` carries
         `AbortSignal.timeout(REQUEST_TIMEOUT_MS)` — fifteen seconds — so
         a slow read can hand back a snapshot that old and the sum
         derived from it is still posted as fact. This store 429s
         readily, which is exactly when reads get slow. A shopper holding
         2 in size 9 and 1 in size 10, who changes the 10 to a 9 and
         presses `+` on the 9 while that is in flight, had their `+`
         overwritten by the stale `2 + 1`: they asked for 4, got 3, and
         were told it succeeded.

         This SHRINKS the window to one round trip rather than closing
         it. Nothing available here can close it. What it buys is that
         the remainder is milliseconds of network instead of the fifteen
         seconds the timeout permits. */
      const fresh = await fetchCart();
      const freshSource = fresh.items.find((item) => item.key === lineKey);
      const freshTwin = fresh.items.find((item) => item.key === twin.key);

      /* A MISSING LINE COUNTS AS MOVED. A concurrent removal, or Redo
         rewriting the cart, leaves nothing to merge into — and reading
         `undefined.quantity` a line later would throw into the catch,
         which reverts the select and shows a stack-shaped message. */
      const moved =
        !freshSource ||
        !freshTwin ||
        freshSource.quantity !== quantity ||
        freshTwin.quantity !== twin.quantity;

      /* ABORT RATHER THAN ABSORB. Recomputing the sum from the fresh
         read and writing it anyway would complete the swap and read as
         tidier, but it folds a change the shopper made for their own
         reasons into a larger write they never saw — and it carries the
         identical residual race, so it buys nothing for that risk.
         Re-render the truth and ask them to repeat the swap against it. */
      if (moved) {
        /* The select is showing a size the cart does not have and now
           never will. Left alone it is a lie that survives to the next
           page load — the same reason the catch below reverts it. */
        select.value = previousVariantId;

        /* NO RE-RENDER HERE, AND THE OMISSION IS THE DELIBERATE PART.
           Redrawing the section so the shopper sees the cart we just
           read is the obvious move, and it cannot be taken safely from
           this path.

           `restoreFocus` deliberately does not focus "whatever is
           there" — it waits for a POSITIVE marker that the morph has
           happened, namely a select whose `data-current-variant` reports
           the variant the row ends up with. An abort changes no
           variant, so that marker is ALREADY TRUE on the pre-morph DOM:
           the hand-off would match the very select the re-render is
           about to destroy, focus it, and land focus on <body> a frame
           later. That is this theme's twice-shipped defect, reached by a
           third route.

           Re-render nothing and focus never moves, because the select
           the shopper is using is never replaced. What that costs is a
           cart display that can lag by one concurrent change until the
           next render — and usually not even that, since the concurrent
           writer is nearly always the shopper's own stepper, which
           re-renders the section itself on the way through.

           DEV-123 proposes re-rendering here. Doing it needs
           `restoreFocus` to accept a marker other than the variant id —
           element identity across the morph is the obvious candidate —
           and that is not worth designing blind: the merge branch has
           never once executed in a browser (see the note below) and this
           store blocks the walkthrough harness that would show it. */

        /* Deferred, not written inline, even though nothing here
           re-renders: the concurrent change that triggered this abort
           may have a re-render of its own still in flight, and that
           would wipe an inline message. `showLineMessageAfterRender`
           covers both orderings so the caller cannot pick the wrong
           one. */
        showLineMessageAfterRender(
          surface,
          line,
          'Your cart changed while this was saving — please try the size change again.'
        );
      } else {
        /* THE SUM THE SHOPPER ASKED FOR, from the fresh read — the only
           two quantities the guard above has just proved current. It is
           named because it is used twice and the two uses must not drift:
           it is what gets written, and it is what the response is checked
           against. Recomputing the second one from the stale snapshot is
           precisely the defect this block closes. */
        const expected = freshTwin.quantity + freshSource.quantity;

        /* ONE REQUEST FOR BOTH CHANGES. `/cart/update.js` takes an
           `updates` map keyed by line key and applies it as a unit, so
           the shopper can never be observed holding the summed quantity
           twice or neither. This is the only place in the swap where
           atomicity is actually available; take it. */
        const updated = await postJson('cart/update.js', {
          updates: {
            [twin.key]: expected,
            [lineKey]: 0,
          },
          attributes: {
            /* THE FRESH CART, NOT THE ONE READ AT THE TOP. This attribute
               is a full ordered list of the cart's variant ids, so it
               goes stale on any line the guard above does not watch — and
               one such line is guaranteed: Redo's coverage line arrives
               about a second after load and moves with later edits. Built
               from the stale read it would write an order with the
               coverage line missing. */
            [LINE_ORDER_ATTRIBUTE]: lineOrderAfterSwap(fresh, lineKey, null),
          },
        });
        /* SHOPIFY CLAMPS, AND SAYS SO ONLY IN THE RESPONSE. Unlike
           `/cart/add.js`, which 422s when stock runs out, `/cart/update.js`
           caps the quantity at what is available and returns 200 with the
           capped value. Nothing compared the two, so a shopper merging
           2 + 1 into a line with only 2 in stock silently ended up with 2
           — one fewer boot than they had a moment earlier, reported as a
           clean success.

           Not an error: the swap DID happen and the cart is consistent.
           It is a notice, in the row's own error cell, because a quantity
           changing without being asked is exactly the thing a shopper
           needs told. */
        const mergedLine = updated.items.find((item) => item.key === twin.key);
        /* COMPARED AGAINST `expected`, WHICH CAME FROM THE FRESH READ.
           Against the stale sum this test conflated two different events:
           a quantity Shopify capped for stock, and a quantity that came
           back lower because a concurrent change had been overwritten. It
           could therefore explain a lost `+` as a stock shortage, or —
           when the two cancelled out — say nothing at all. */
        if (mergedLine && mergedLine.quantity < expected) {
          /* HELD UNTIL AFTER THE RE-RENDER, not written now. The error
             cell lives inside the section `rerender` is about to replace,
             so a message written here survives about 300ms — which is the
             second half of the P0 this file just fixed, and would have
             been reintroduced by writing the notice inline. */
          notice = `Only ${mergedLine.quantity} available — your cart has been updated.`;
        }

        rerender(updated);
        swapped = true;
      }
    } else {
      /* NOT `return` — AN `else`. The merge branch used to return from
         inside the `try`, which runs the `finally` and then leaves the
         FUNCTION, so it skipped the focus hand-off at the tail
         entirely. It had carried its own `restoreFocus` call for that
         reason, inside the `try`, which is precisely the placement that
         let `finally`'s `select.disabled = false` wake the observed
         wait early — the defect fixed on the other path. One exit, one
         hand-off, and the merge case stops being a second
         implementation that drifts.

         Never verified in a browser: the merge check needs to seed a
         second size through `/cart/add.js`, which this store 429s. */

      /* ADD FIRST. If this fails the cart is untouched and the select
         reverts below — the shopper has lost nothing. */
      await postJson('cart/add.js', {
        id: Number(targetVariantId),
        quantity,
        properties,
      });
      /* FROM HERE THE CART HAS CHANGED even if the next write fails.
         The catch below has to re-render, or the shopper reads an error
         over a cart that still shows one line while the server holds
         two. */
      addLanded = true;

      /* THEN REMOVE. If THIS fails the shopper holds both sizes, which
         is visible and fixable with the Remove control two lines below.
         That asymmetry is the whole reason for the order. */
      const removed = await postJson('cart/update.js', {
        updates: { [lineKey]: 0 },
        attributes: {
          [LINE_ORDER_ATTRIBUTE]: lineOrderAfterSwap(cart, lineKey, targetVariantId),
        },
      });

      rerender(removed);
      swapped = true;
    }
  } catch (error) {
    /* REVERT THE CONTROL. Left showing the size it failed to apply, the
       select becomes a lie about the cart's contents that survives
       until the next page load. */
    select.value = previousVariantId;
    /* DEFERRED, because the `addLanded` branch below re-renders and
       would wipe this — the exact ordering the notice mechanism was
       built to avoid, on the one path where the shopper most needs the
       message: the add landed, the remove did not, and they are holding
       both sizes. */
    showLineMessageAfterRender(surface, line, error.message);

    /* The add landed and the remove did not, so the shopper holds both
       sizes. Re-render so the row they have to act on is on screen next
       to the error explaining it. Best-effort — if reading the cart back
       also fails there is nothing further to do from here, and the
       message is already shown. */
    if (addLanded) {
      try {
        rerender(await fetchCart());
      } catch {
        /* Leave the error up; a reload will show the real cart. */
      }
    }
  } finally {
    inFlight = false;
    select.disabled = false;
  }

  /* AFTER THE `finally`, AND THAT PLACEMENT IS THE WHOLE POINT.
     `restoreFocus` installs an observed wait on `.cart-page`, and this
     ran inside the `try` — so the very next statement to execute was
     `finally` setting `select.disabled = false`. That select is inside
     `.cart-page`, which is the observer's own root, so the wait settled
     on OUR write while base's re-render was still awaiting its fetch.
     `restore` then focused the OLD select, which the morph destroyed a
     moment later, and focus landed on <body>.

     It presented as flakiness rather than a failure, which is why it
     survived: whether the walkthrough sampled `activeElement` before or
     after the morph decided whether the run was green. The identical
     shape, with the identical cause, was fixed in
     `assets/freebird-cart-undo.js` — the two files share this observer
     deliberately so they cannot drift, and they must share this
     ordering rule too. Nothing may touch `.cart-page` between the
     dispatch and this call. */
  if (swapped) restoreFocus(surface, targetVariantId);

  /* Re-shown once the section has been redrawn, resolving the surface
     from the swapped line itself rather than the captured element —
     `surface` is detached by now. Same observed wait the focus hand-off
     uses, so the two cannot disagree about when the DOM is ready. */
  if (swapped && notice) showLineMessageAfterRender(surface, line, notice);
};

/* Delegated on `document` rather than bound per select. Every write
   here re-renders the section, so the selects that exist now are not
   the ones that will exist afterwards — a directly bound listener dies
   at the first successful swap. Base's own cart components delegate for
   the same reason. */
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) return;
  if (!target.matches(SELECT_SELECTOR)) return;

  if (inFlight) {
    /* Snap back rather than queue. A queued second swap would be
       computed against a cart the first one is still changing. */
    target.value = target.dataset.currentVariant ?? target.value;
    return;
  }

  swap(target);
});
